import { useState, useEffect, useCallback } from 'react'
import { apiService } from '../services/api'
import { Lead, LeadFiltersAPI } from '../types'

export const useLeads = () => {
 const [leads, setLeads] = useState<Lead[]>([])
 const [isLoading, setIsLoading] = useState(true)
 const [error, setError] = useState<string | null>(null)
 const [pagination, setPagination] = useState({
 page: 1,
 limit: 50,
 total: 0,
 totalPages: 0
 })

const fetchLeads = useCallback(async (params?: LeadFiltersAPI) => {
  try {
  setIsLoading(true)
  setError(null)
  const response = await apiService.getLeads(params)
  
  // A API pode retornar { leads: [...], pagination: {...} } ou array direto
  if (response && typeof response === 'object' && 'leads' in response) {
  setLeads(response.leads || [])
  if (response.pagination) {
  setPagination(response.pagination)
  }
  } else if (Array.isArray(response)) {
  // Fallback para resposta direta como array
  setLeads(response)
  } else {
  setLeads([])
  }
  } catch (err) {
  console.error('Erro ao carregar leads:', err)
  setError('Erro ao carregar leads. Tente novamente.')
  setLeads([])
  } finally {
  setIsLoading(false)
  }
  }, [])

 const createLead = useCallback(async (leadData: Partial<Lead>) => {
 try {
 const newLead = await apiService.createLead(leadData)
 if (newLead) {
 setLeads(prev => [newLead, ...prev])
 return newLead
 }
 throw new Error('Lead criado mas sem dados retornados')
 } catch (err) {
 console.error('Erro ao criar lead:', err)
 setError('Erro ao criar lead. Tente novamente.')
 throw err
 }
 }, [])

 const updateLead = useCallback(async (id: string, leadData: Partial<Lead>) => {
 try {
 const updatedLead = await apiService.updateLead(id, leadData)
 if (updatedLead) {
 setLeads(prev => prev.map(lead => lead.id === id ? updatedLead : lead))
 return updatedLead
 }
 throw new Error('Lead atualizado mas sem dados retornados')
 } catch (err) {
 console.error('Erro ao atualizar lead:', err)
 setError('Erro ao atualizar lead. Tente novamente.')
 throw err
 }
 }, [])

 const deleteLead = useCallback(async (id: string) => {
 try {
 await apiService.deleteLead(id)
 setLeads(prev => prev.filter(lead => lead.id !== id))
 } catch (err) {
 console.error('Erro ao deletar lead:', err)
 setError('Erro ao deletar lead. Tente novamente.')
 throw err
 }
 }, [])

 const updateLeadStage = useCallback(async (id: string, stageId: string) => {
 try {
 const updatedLead = await apiService.updateLead(id, { funnelStageId: stageId })
 if (updatedLead) {
 setLeads(prev => prev.map(lead => lead.id === id ? updatedLead : lead))
 return updatedLead
 }
 throw new Error('Lead atualizado mas sem dados retornados')
 } catch (err) {
 console.error('Erro ao atualizar stage do lead:', err)
 setError('Erro ao atualizar etapa do lead. Tente novamente.')
 throw err
 }
 }, [])

 useEffect(() => {
 fetchLeads()
 }, [])

 return {
 leads,
 isLoading,
 error,
 pagination,
 fetchLeads,
 createLead,
 updateLead,
 deleteLead,
 updateLeadStage
 }
}

